/**
 * FRONT-2349: "Edit" on a Reflections proposal — the card's draft state and
 * the pure transforms on it. Shared by the Memory page's Reflections cards
 * and the onboarding recap's per-row Edit.
 *
 * What goes back is an EDIT, never a payload: the server rebuilds the
 * proposal from its stored original (`src/reflect/proposal-edit.ts`), so the
 * target of the change can't be moved from here. A new entry reuses the
 * Quick OK card's memory-form draft (FRONT-1747) as-is, so its name, type
 * and fact rows behave exactly like the approval card's.
 *
 * Plain functions, because the frontend suite renders to static markup with
 * no DOM: every transform a click triggers is tested by calling it.
 */
import type { ReflectProposal } from '~/hooks/queries/use-reflect';
import { ApiError } from '~/lib/api-client';
import {
  draftBlocker,
  draftFromForm,
  editFromDraft,
  type ApprovalDraft,
} from '~/features/chat/approval-edit-draft';

/** Mirrors `ReflectProposalEdit` in `src/reflect/proposal-edit.ts`. */
export type ReflectProposalEdit =
  | { kind: 'entity_create'; name: string; entityType: string; attributes: Record<string, string> }
  | { kind: 'attribute_update'; value: string }
  | { kind: 'perfect_day_update'; fields: Record<string, string | string[]> };

export type ReflectionDraft =
  | { kind: 'entity_create'; memory: Extract<ApprovalDraft, { kind: 'memory' }> }
  | { kind: 'attribute_update'; label: string; value: string }
  | {
      kind: 'perfect_day_update';
      fields: Array<{
        key: string;
        label: string;
        /** A list field (must-dos): edited one item per line. */
        list: boolean;
        value: string;
      }>;
    };

/** Friendly labels for the Perfect Day fields — mirrors `summarize` in `src/onboarding/tool.ts`. */
export const PERFECT_DAY_FIELD_LABELS: Record<string, string> = {
  identity: 'Role',
  northStar: 'What a good day looks like',
  mustDos: 'Must-dos',
  focus: 'Focus',
  loops: 'What "done" means',
  autonomy: 'Autonomy',
  archetype: 'Archetype',
};

/**
 * The Perfect Day budgets the server holds an edit to — mirrors `SPEC_LIMITS`
 * in `src/agents/perfect-day-spec.ts` (the two apps don't share a build), so
 * an over-long correction is refused here with a reason instead of by a 400.
 */
export const PERFECT_DAY_LIMITS = { textMax: 500, mustDosMax: 7, mustDoItemMax: 100 } as const;

function humanize(raw: string): string {
  const s = raw.replace(/_/g, ' ');
  return s.length > 0 ? s[0].toUpperCase() + s.slice(1) : s;
}

function perfectDayFields(p: Pick<ReflectProposal, 'payload'>): Record<string, unknown> | null {
  const fields = p.payload?.fields;
  return fields && typeof fields === 'object' && !Array.isArray(fields) ? (fields as Record<string, unknown>) : null;
}

/**
 * True when this proposal offers Edit. Fails closed: a payload it can't read
 * cleanly keeps Accept/Reject only rather than showing a form that would
 * drop part of it. The people list never gets Edit — it moves together with
 * each person's own entry and connection.
 */
export function reflectionEditable(p: Pick<ReflectProposal, 'kind' | 'payload'>): boolean {
  const pl = p.payload ?? {};
  switch (p.kind) {
    case 'entity_create':
      return typeof pl.name === 'string' && typeof pl.entityType === 'string';
    case 'attribute_update':
      return typeof pl.key === 'string' && typeof pl.value === 'string';
    case 'perfect_day_update': {
      const fields = perfectDayFields(p);
      if (!fields) return false;
      const entries = Object.entries(fields);
      return (
        entries.length > 0 &&
        entries.every(
          ([k, v]) =>
            k !== 'people' &&
            // A list item holding its own line break can't round-trip through
            // the one-item-per-line box, so that list keeps Accept/Reject only.
            (typeof v === 'string' ||
              (Array.isArray(v) && v.every((x) => typeof x === 'string' && !x.includes('\n')))),
        )
      );
    }
    default:
      return false;
  }
}

export function draftFromProposal(p: Pick<ReflectProposal, 'kind' | 'payload'>): ReflectionDraft | null {
  if (!reflectionEditable(p)) return null;
  const pl = p.payload ?? {};
  if (p.kind === 'entity_create') {
    const attributes = Array.isArray(pl.attributes) ? (pl.attributes as Array<Record<string, unknown>>) : [];
    const memory = draftFromForm({
      kind: 'memory',
      entities: [
        {
          index: 0,
          existing: false,
          name: pl.name as string,
          type: pl.entityType as string,
          attributes: attributes.map((a) => ({
            category: String(a.category ?? 'general'),
            key: String(a.key ?? ''),
            value: String(a.value ?? ''),
          })),
        },
      ],
    });
    return { kind: 'entity_create', memory: memory as Extract<ApprovalDraft, { kind: 'memory' }> };
  }
  if (p.kind === 'attribute_update') {
    return { kind: 'attribute_update', label: humanize(pl.key as string), value: pl.value as string };
  }
  const fields = perfectDayFields(p)!;
  return {
    kind: 'perfect_day_update',
    fields: Object.entries(fields).map(([key, v]) => ({
      key,
      label: PERFECT_DAY_FIELD_LABELS[key] ?? humanize(key),
      list: Array.isArray(v),
      value: Array.isArray(v) ? v.join('\n') : (v as string),
    })),
  };
}

export function setPerfectDayField(
  draft: Extract<ReflectionDraft, { kind: 'perfect_day_update' }>,
  key: string,
  value: string,
): ReflectionDraft {
  return { ...draft, fields: draft.fields.map((f) => (f.key === key ? { ...f, value } : f)) };
}

function listItems(value: string): string[] {
  return value
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

/**
 * The one reason "Save this version" is unavailable, or `null` when it can be
 * sent — the button is disabled exactly when there is a sentence to show
 * (FRONT-1670).
 */
export function reflectionDraftBlocker(draft: ReflectionDraft): string | null {
  if (draft.kind === 'entity_create') {
    if (draft.memory.entities.length === 0) return 'Every entry needs a name.';
    return draftBlocker(draft.memory);
  }
  if (draft.kind === 'attribute_update') {
    return draft.value.trim() ? null : `The fact "${draft.label}" needs a value.`;
  }
  for (const f of draft.fields) {
    if (f.list) {
      const items = listItems(f.value);
      if (items.length === 0) return `${f.label} can't be empty. Choose Reject instead.`;
      if (items.length > PERFECT_DAY_LIMITS.mustDosMax) {
        return `Keep ${f.label} to ${PERFECT_DAY_LIMITS.mustDosMax} items or fewer.`;
      }
      if (items.some((i) => i.length > PERFECT_DAY_LIMITS.mustDoItemMax)) {
        return `Each item can be at most ${PERFECT_DAY_LIMITS.mustDoItemMax} characters.`;
      }
    } else {
      const value = f.value.trim();
      if (!value) return `${f.label} can't be empty. Choose Reject instead.`;
      if (value.length > PERFECT_DAY_LIMITS.textMax) {
        return `${f.label} can be at most ${PERFECT_DAY_LIMITS.textMax} characters.`;
      }
    }
  }
  return null;
}

/** What "Save this version" sends. */
export function editFromReflectionDraft(draft: ReflectionDraft): ReflectProposalEdit {
  if (draft.kind === 'entity_create') {
    const edit = editFromDraft(draft.memory);
    const entry = edit.kind === 'memory' ? edit.entities[0] : undefined;
    return {
      kind: 'entity_create',
      name: entry?.name ?? '',
      entityType: entry?.type ?? '',
      attributes: entry?.attributes ?? {},
    };
  }
  if (draft.kind === 'attribute_update') return { kind: 'attribute_update', value: draft.value };
  return {
    kind: 'perfect_day_update',
    fields: Object.fromEntries(draft.fields.map((f) => [f.key, f.list ? listItems(f.value) : f.value])),
  };
}

/** What the edit form says after a Save that didn't land. */
export function editFailureCopy(err: unknown): string {
  // A 400 is the server's own sentence about the edit (written for the
  // person); anything else means the proposal was decided elsewhere or gone.
  if (err instanceof ApiError && err.status === 400) return err.message;
  return "That didn't go through, nothing was changed. It may already have been decided.";
}
